import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SECTION_ORDER = ['home', 'about', 'skills', 'projects', 'certificates', 'contact'];

function PageIndicator({ currentSection, prevSection, nextSection }) {
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleActivity = () => {
      setIsVisible(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 2000);
    };

    // Sayfa yüklendiğinde ve scroll olayında aktivite algılama
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('scroll', handleActivity);
    handleActivity(); // İlk yüklemede timeout'u başlat

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sectionNumber = (id) =>
    String(SECTION_ORDER.indexOf(id) + 1).padStart(2, '0');

  const handleNavigate = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.div
      className="fixed left-8 top-1/3 -translate-y-1/2 z-40 hidden md:flex flex-col items-start gap-4"
      initial={{ opacity: 0, x: -20 }}
      animate={{
        opacity: isVisible ? 1 : 0.1,
        x: 0
      }}
      whileHover={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Önceki Sayfa */}
      {prevSection && (
        <motion.div
          className="group cursor-pointer scale-90"
          onClick={() => handleNavigate(prevSection)}
          whileHover={{ x: 5, scale: 0.95 }}
          whileTap={{ scale: 0.85 }}
        >
          <div className="relative flex items-center">
            <div className="absolute -left-2 w-1 h-10 rounded-full bg-arcade-purple/50 group-hover:bg-arcade-cyan transition-colors" />
            <div className="pl-4 py-1">
              <div className="flex items-center gap-3 bg-arcade-panel/80 backdrop-blur-sm px-3 py-2 rounded border border-arcade-purple/30 group-hover:border-arcade-cyan/60 transition-all">
                <span className="font-pixel text-[10px] text-arcade-purple group-hover:text-arcade-cyan transition-colors">
                  {sectionNumber(prevSection)}
                </span>
                <div className="flex flex-col">
                  <span className="font-pixel text-[7px] text-arcade-purple/80 mb-1">
                    ▲ {t('navigation.previous').toUpperCase()}
                  </span>
                  <span className="font-mono text-xs text-gray-200 tracking-wider">
                    {t(`navbar.${prevSection}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Aktif Sayfa */}
      <div className="relative pl-4 py-2">
        <div className="absolute -left-2 w-1 h-16 rounded-full bg-arcade-cyan shadow-[0_0_8px_rgba(77,232,244,0.7)]" />
        <div className="flex items-center gap-3 bg-arcade-panel/90 backdrop-blur-sm px-4 py-3 rounded border border-arcade-cyan/60 shadow-[0_0_14px_rgba(77,232,244,0.25)]">
          <span className="font-pixel text-sm text-arcade-cyan drop-shadow-[0_0_6px_rgba(77,232,244,0.6)]">
            {sectionNumber(currentSection)}
          </span>
          <div className="flex flex-col">
            <span className="font-pixel text-[7px] text-arcade-yellow mb-1 animate-pulse">
              ▶ {t('navigation.current').toUpperCase()}
            </span>
            <span className="font-mono text-sm text-white tracking-wider">
              {t(`navbar.${currentSection}`)}
            </span>
          </div>
        </div>
      </div>

      {/* Sonraki Sayfa */}
      {nextSection && (
        <motion.div
          className="group cursor-pointer scale-90"
          onClick={() => handleNavigate(nextSection)}
          whileHover={{ x: 5, scale: 0.95 }}
          whileTap={{ scale: 0.85 }}
        >
          <div className="relative flex items-center">
            <div className="absolute -left-2 w-1 h-10 rounded-full bg-arcade-purple/50 group-hover:bg-arcade-cyan transition-colors" />
            <div className="pl-4 py-1">
              <div className="flex items-center gap-3 bg-arcade-panel/80 backdrop-blur-sm px-3 py-2 rounded border border-arcade-purple/30 group-hover:border-arcade-cyan/60 transition-all">
                <span className="font-pixel text-[10px] text-arcade-purple group-hover:text-arcade-cyan transition-colors">
                  {sectionNumber(nextSection)}
                </span>
                <div className="flex flex-col">
                  <span className="font-pixel text-[7px] text-arcade-purple/80 mb-1">
                    ▼ {t('navigation.next').toUpperCase()}
                  </span>
                  <span className="font-mono text-xs text-gray-200 tracking-wider">
                    {t(`navbar.${nextSection}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default PageIndicator;
